import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { OperatingHours } from '@kanzi-apes/kanzi-models';

/**
 * Internal model for schedule component rows
 */
interface DayScheduleRow {
  dayOfWeek: number;
  dayKey: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
  dayNameKey: string;
  openingTime: string;
  closingTime: string;
  openingTimeDate: Date | null;
  closingTimeDate: Date | null;
  isClosed: boolean;
}

@Component({
    selector: 'kanzi-apes-operation-schedule',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        CalendarModule,
        CheckboxModule,
    ],
    templateUrl: './operation-schedule.component.html',
    styleUrls: ['./operation-schedule.component.scss']
})
export class OperationScheduleComponent implements OnInit {
  @Input() operatingHours: OperatingHours | null = null;
  @Input() disabled = false;

  @Output() operatingHoursChange = new EventEmitter<OperatingHours>();

  daySchedules: DayScheduleRow[] = [];

  private readonly dayNameKeys = [
    'common.days.monday',
    'common.days.tuesday',
    'common.days.wednesday',
    'common.days.thursday',
    'common.days.friday',
    'common.days.saturday',
    'common.days.sunday',
  ];

  private readonly dayKeys: Array<'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'> = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

  ngOnInit(): void {
    this.initializeSchedules();
  }

  /**
   * Initialize 7-day schedule array (Monday-Sunday)
   */
  private initializeSchedules(): void {
    this.daySchedules = [];

    for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
      const dayKey = this.dayKeys[dayOfWeek];
      
      let openingTime = '00:00';
      let closingTime = '00:00';
      let isClosed = false;

      // If operatingHours is provided (edit mode)
      if (this.operatingHours) {
        // Check if this specific day has hours defined
        if (this.operatingHours[dayKey]) {
          const dayHours = this.operatingHours[dayKey];
          if (dayHours) {
            openingTime = dayHours.open_time;
            closingTime = dayHours.close_time;
          }
        } else {
          // Day exists in operatingHours object but has no hours = closed
          isClosed = true;
        }
      }
      // If operatingHours is null/undefined (create mode), use defaults (isClosed = true, times = 00:00)

      this.daySchedules.push({
        dayOfWeek,
        dayKey,
        dayNameKey: this.dayNameKeys[dayOfWeek],
        openingTime,
        closingTime,
        openingTimeDate: this.timeStringToDate(openingTime),
        closingTimeDate: this.timeStringToDate(closingTime),
        isClosed,
      });
    }
  }

  /**
   * Convert time string (HH:MM) to Date object for p-calendar
   */
  private timeStringToDate(timeStr: string): Date | null {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  /**
   * Convert Date object to time string (HH:MM)
   */
  private dateToTimeString(date: Date | null): string {
    if (!date) return '00:00';
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  /**
   * Handle opening time change from calendar
   */
  onOpeningTimeChange(day: DayScheduleRow): void {
    day.openingTime = this.dateToTimeString(day.openingTimeDate);
    this.onScheduleChange();
  }

  /**
   * Handle closing time change from calendar
   */
  onClosingTimeChange(day: DayScheduleRow): void {
    day.closingTime = this.dateToTimeString(day.closingTimeDate);
    this.onScheduleChange();
  }

  /**
   * Handle changes to schedule data and emit to parent
   */
  onScheduleChange(): void {
    const operatingHours: OperatingHours = {};
    
    this.daySchedules.forEach((day) => {
      if (!day.isClosed) {
        operatingHours[day.dayKey] = {
          open_time: day.openingTime,
          close_time: day.closingTime,
        };
      }
    });

    this.operatingHoursChange.emit(operatingHours);
  }

  /**
   * Validate if opening time is before closing time
   */
  isTimeRangeValid(day: DayScheduleRow): boolean {
    if (day.isClosed) {
      return true;
    }
    return day.openingTime < day.closingTime;
  }

  /**
   * Get validation error message for a day
   */
  getValidationError(day: DayScheduleRow): string | null {
    if (!day.isClosed && !this.isTimeRangeValid(day)) {
      return 'La hora de apertura debe ser anterior a la hora de cierre';
    }
    return null;
  }
}